import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { validateResponse } from "@/api/validateResponse";
import {
  CalendarResponseSchema,
  EventListResponseSchema,
  EventSchema,
  type CalendarResponse,
  type Event,
  type EventStatus,
  type PatchEventInput,
  type PatchEventTaskInput,
  type RescheduleEventInput,
  type SetCaptureInput,
  type SetEventContractInput,
  type SetEventStatusInput,
  type WaiveCaptureInput,
} from "@/api/types/event";

/** Query-key factory — mirrors the quotes/inquiries hook convention. */
export const eventKeys = {
  all: ["events"] as const,
  lists: () => [...eventKeys.all, "list"] as const,
  list: (params: { status?: EventStatus; from?: string; to?: string; limit?: number }) =>
    [...eventKeys.lists(), params] as const,
  details: () => [...eventKeys.all, "detail"] as const,
  detail: (id: string | undefined) => [...eventKeys.details(), id] as const,
  calendar: (month: string) => [...eventKeys.all, "calendar", month] as const,
};

function invalidateAll(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: eventKeys.all });
}

interface UseEventsParams {
  status?: EventStatus;
  from?: string;
  to?: string;
  limit?: number;
}

/** List query with cursor pagination, mirroring useContacts'/useInquiries' convention. */
export function useEvents(params: UseEventsParams = {}) {
  const { status, from, to, limit = 25 } = params;

  return useInfiniteQuery({
    queryKey: eventKeys.list({ status, from, to, limit }),
    queryFn: async ({ pageParam }) => {
      const searchParams = new URLSearchParams();
      if (status) searchParams.set("status", status);
      if (from) searchParams.set("date_from", from);
      if (to) searchParams.set("date_to", to);
      searchParams.set("limit", String(limit));
      if (pageParam) searchParams.set("cursor", pageParam);

      const { data } = await apiClient.get(`/v2/events?${searchParams.toString()}`);
      return validateResponse(EventListResponseSchema, data, "GET /v2/events");
    },
    initialPageParam: "" as string,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    staleTime: 15 * 1000,
  });
}

export function useEvent(eventId: string | undefined) {
  return useQuery<Event>({
    queryKey: eventKeys.detail(eventId),
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/events/${eventId}`);
      return validateResponse(EventSchema, data, "GET /v2/events/:id");
    },
    enabled: !!eventId,
  });
}

export function useEventsCalendar(month: string) {
  return useQuery<CalendarResponse>({
    queryKey: eventKeys.calendar(month),
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/events/calendar?month=${month}`);
      return validateResponse(
        CalendarResponseSchema,
        data,
        "GET /v2/events/calendar",
      );
    },
    staleTime: 15 * 1000,
  });
}

export function usePatchEvent() {
  const queryClient = useQueryClient();
  return useMutation<Event, unknown, PatchEventInput>({
    mutationFn: async ({ id, ...patch }) => {
      const { data } = await apiClient.patch(`/v2/events/${id}`, patch);
      return validateResponse(EventSchema, data, "PATCH /v2/events/:id");
    },
    onSuccess: (event) => {
      queryClient.setQueryData(eventKeys.detail(event.id), event);
      invalidateAll(queryClient);
    },
  });
}

export function useRescheduleEvent() {
  const queryClient = useQueryClient();
  return useMutation<Event, unknown, RescheduleEventInput>({
    mutationFn: async ({ id, event_date }) => {
      const { data } = await apiClient.post(`/v2/events/${id}/reschedule`, {
        event_date,
      });
      return validateResponse(EventSchema, data, "POST /v2/events/:id/reschedule");
    },
    onSuccess: (event) => {
      queryClient.setQueryData(eventKeys.detail(event.id), event);
      invalidateAll(queryClient);
    },
  });
}

/**
 * Task update returns the parent Event (with its re-fetched tasks[]), not a
 * bare task — that's the simplest contract for the UI to reconcile against
 * (one cache write, no separate task cache to keep in sync).
 */
export function usePatchEventTask() {
  const queryClient = useQueryClient();
  return useMutation<Event, unknown, PatchEventTaskInput>({
    mutationFn: async ({ eventId, taskId, ...patch }) => {
      const { data } = await apiClient.patch(
        `/v2/events/${eventId}/tasks/${taskId}`,
        patch,
      );
      return validateResponse(
        EventSchema,
        data,
        "PATCH /v2/events/:id/tasks/:task_id",
      );
    },
    onSuccess: (event) => {
      queryClient.setQueryData(eventKeys.detail(event.id), event);
      invalidateAll(queryClient);
    },
  });
}

function useEventAction(endpoint: string, label: string) {
  const queryClient = useQueryClient();
  return useMutation<Event, unknown, { id: string; body?: Record<string, unknown> }>({
    mutationFn: async ({ id, body }) => {
      const { data } = await apiClient.post(`/v2/events/${id}/${endpoint}`, body ?? {});
      return validateResponse(EventSchema, data, `POST /v2/events/:id/${label}`);
    },
    onSuccess: (event) => {
      queryClient.setQueryData(eventKeys.detail(event.id), event);
      invalidateAll(queryClient);
    },
  });
}

export function useMarkDepositPaid() {
  return useEventAction("deposit-paid", "deposit-paid");
}

export function useMarkBalancePaid() {
  return useEventAction("balance-paid", "balance-paid");
}

export function useSetEventContract() {
  const queryClient = useQueryClient();
  return useMutation<Event, unknown, SetEventContractInput>({
    mutationFn: async ({ id, contract_key }) => {
      const { data } = await apiClient.post(`/v2/events/${id}/contract`, {
        contract_key,
      });
      return validateResponse(EventSchema, data, "POST /v2/events/:id/contract");
    },
    onSuccess: (event) => {
      queryClient.setQueryData(eventKeys.detail(event.id), event);
      invalidateAll(queryClient);
    },
  });
}

export function useSetEventStatus() {
  const queryClient = useQueryClient();
  return useMutation<Event, unknown, SetEventStatusInput>({
    mutationFn: async ({ id, status }) => {
      const { data } = await apiClient.post(`/v2/events/${id}/status`, { status });
      return validateResponse(EventSchema, data, "POST /v2/events/:id/status");
    },
    onSuccess: (event) => {
      queryClient.setQueryData(eventKeys.detail(event.id), event);
      invalidateAll(queryClient);
    },
    // 422 (capture-checklist gate on `delivered`) is left to reject — the
    // caller (EventDetailPage) reads the error's `detail` message and shows
    // it inline, per the plan's "surface the 422 detail" requirement.
  });
}

export function useSetEventCapture() {
  const queryClient = useQueryClient();
  return useMutation<Event, unknown, SetCaptureInput>({
    mutationFn: async ({ id, key, value }) => {
      const { data } = await apiClient.post(`/v2/events/${id}/capture`, { key, value });
      return validateResponse(EventSchema, data, "POST /v2/events/:id/capture");
    },
    onSuccess: (event) => {
      queryClient.setQueryData(eventKeys.detail(event.id), event);
      invalidateAll(queryClient);
    },
  });
}

export function useWaiveEventCapture() {
  const queryClient = useQueryClient();
  return useMutation<Event, unknown, WaiveCaptureInput>({
    mutationFn: async ({ id, reason }) => {
      const { data } = await apiClient.post(`/v2/events/${id}/capture/waive`, {
        reason,
      });
      return validateResponse(
        EventSchema,
        data,
        "POST /v2/events/:id/capture/waive",
      );
    },
    onSuccess: (event) => {
      queryClient.setQueryData(eventKeys.detail(event.id), event);
      invalidateAll(queryClient);
    },
  });
}
